import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Button from 'react-bootstrap/Button';
import Divider from '../../components/divider/divider.component';
import ExpenseModal from './parts/expense-modal.component';
import Table from '../../components/table/table.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import { setCurrentPage } from '../../redux/entities/admin/expenses';
import { getExpenses, deleteExpenses } from '../../redux/thunks/admin/expenses';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { RowButtons } from '../../components/table/table.styles';
import {
  expenseCategories,
  recurringFrequencies,
} from '../../utils/form-configs/expenses.form';

const ExpensesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { expenses, currentPage, limit, totalDocs, isUpdating, isFetching } =
    useSelector((state) => state.entities.admin.expenses);
  const [showAddModal, setShowAddModal] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  const addingModalTypes = {
    normal: 'normal',
    userMonthlySalary: 'userMonthlySalary',
  };

  useEffect(() => {
    dispatch(
      getExpenses({
        page: currentPage,
        limit,
        search: searchValue,
        dateRange,
      })
    );
  }, [currentPage, limit, searchValue, dateRange]);

  useDebounce(
    () => {
      dispatch(setCurrentPage(1));
    },
    [searchValue, dateRange],
    500
  );

  const handleDelete = (ids) => {
    const onSuccess = (message) => {
      enqueueSnackbar(message, { variant: 'success' });
      setExpenseToDelete(null);
    };
    const onFailure = (message) =>
      enqueueSnackbar(message, { variant: 'error' });

    dispatch(deleteExpenses({ ids, onSuccess, onFailure }));
  };

  const handleEdge = useCallback(() => {
    if (currentPage * limit < totalDocs) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [currentPage, limit, totalDocs]);

  return (
    <>
      {showAddModal && (
        <ExpenseModal
          show={showAddModal}
          type={showAddModal}
          onClose={() => setShowAddModal(null)}
        />
      )}

      {expenseToEdit && (
        <ExpenseModal
          show={expenseToEdit}
          type={expenseToEdit ? expenseToEdit.type : null}
          onClose={() => setExpenseToEdit(null)}
          expense={expenseToEdit}
        />
      )}

      <ConfirmModal
        show={expenseToDelete}
        onClose={() => setExpenseToDelete(null)}
        onConfirm={() => handleDelete([expenseToDelete])}
      />

      <Table
        title='Expenses'
        showAmount={true}
        amount={totalDocs}
        headerButtons={
          <>
            <Button
              variant='primary'
              onClick={() => setShowAddModal(addingModalTypes.normal)}
              style={{
                width: '-webkit-fill-available',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              Add an Expense
            </Button>
            <Button
              variant='primary'
              onClick={() =>
                setShowAddModal(addingModalTypes.userMonthlySalary)
              }
              style={{
                width: '-webkit-fill-available',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              Add a Monthly Salary
            </Button>
          </>
        }
        headerItems={[
          { label: 'Name' },
          { label: 'Amount' },
          { label: 'Category' },
          { label: 'Date' },
          { label: 'End Date' },
          { label: 'Recurring Frequently' },
          { label: '' },
        ]}
        onSearch={setSearchValue}
        onDateRangeChange={(start, end, label) =>
          setDateRange({
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          })
        }
        onEdge={handleEdge}
        isLoading={isFetching}
      >
        {expenses.map((expense) => {
          const selectedCategory = expenseCategories.find(
            (category) => category.value === expense.category
          );
          const selectedFrequency = recurringFrequencies.find(
            (frequency) => frequency.value === expense.recurringFrequency
          );

          return (
            <>
              <div key={expense._id}>
                <span>{expense.name}</span>
                <span>{expense.amount}</span>
                <span>
                  {selectedCategory ? selectedCategory.label : 'Unknown'}
                </span>
                <span>
                  {new Date(expense.startedAt).toISOString().split('T')[0]}
                </span>
                <span>
                  {expense.finishedAt
                    ? new Date(expense.finishedAt).toISOString().split('T')[0]
                    : '-'}
                </span>
                <span>
                  {selectedFrequency ? selectedFrequency.label : 'Unknown'}
                </span>
                <RowButtons>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => setExpenseToEdit(expense)}
                    disabled={isUpdating[expense._id]}
                  >
                    Edit
                  </Button>
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={() => setExpenseToDelete(expense._id)}
                    disabled={isUpdating[expense._id]}
                  >
                    Delete
                  </Button>
                </RowButtons>
              </div>
              <Divider />
            </>
          );
        })}
      </Table>
    </>
  );
};

export default ExpensesPage;
